import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { History } from "lucide-react";
import { useEstimate } from "@/context/EstimateContext";
import { PRESETS, CUSTOM_META, COIN_PRESETS } from "@/data/presets";
import { resolveIcon } from "@/lib/icons";
import { formatNum } from "@/lib/format";

export function AddLineItemDialog({ open, onOpenChange, projectId, editing }) {
  const { addItem, updateItem, projects } = useEstimate();
  const [mode, setMode] = useState("templates");
  const [selectedId, setSelectedId] = useState(null);
  const [name, setName] = useState("");
  const [coins, setCoins] = useState("");
  const [error, setError] = useState("");

  // ── Smart presets derived from the user's own logged usage ──
  const allItems = useMemo(() => projects.flatMap((p) => p.items), [projects]);
  // Personal average coins per category (learned from history).
  const catAvg = useMemo(() => {
    const buckets = {};
    for (const it of allItems) {
      if (!(it.coins > 0)) continue;
      (buckets[it.category] ||= []).push(it.coins);
    }
    const out = {};
    for (const [cat, vals] of Object.entries(buckets)) {
      out[cat] = Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
    }
    return out;
  }, [allItems]);
  // Recently used distinct coin values (most-recent first; items are prepended).
  const recentCoins = useMemo(() => {
    const seen = [];
    for (const it of allItems) {
      if (it.coins > 0 && !seen.includes(it.coins)) seen.push(it.coins);
      if (seen.length >= 5) break;
    }
    return seen;
  }, [allItems]);
  // Merge recent values ahead of the generic quick-picks.
  const coinPicks = useMemo(() => {
    const merged = [...recentCoins];
    for (const v of COIN_PRESETS) if (!merged.includes(v)) merged.push(v);
    return merged.slice(0, 8);
  }, [recentCoins]);

  useEffect(() => {
    if (!open) return;
    setError("");
    if (editing) {
      setMode("manual");
      setSelectedId(null);
      setName(editing.name);
      setCoins(String(editing.coins));
    } else {
      setMode("templates");
      setSelectedId(null);
      setName("");
      setCoins("");
    }
  }, [open, editing]);

  const pickPreset = (p) => {
    setSelectedId(p.id);
    setName(p.name);
    // Prefer the user's learned average for this category, else the static default.
    setCoins(String(catAvg[p.category] ?? p.defaultCoins));
    setError("");
  };

  const save = () => {
    const trimmed = name.trim();
    const numCoins = parseInt(coins, 10);
    if (!trimmed) return setError("Please enter a task name.");
    if (!Number.isFinite(numCoins) || numCoins <= 0)
      return setError("Enter the number of coins (greater than 0).");

    if (editing) {
      updateItem(projectId, editing.id, { name: trimmed, coins: numCoins });
    } else {
      const preset = mode === "templates" ? PRESETS.find((p) => p.id === selectedId) : undefined;
      addItem(projectId, {
        name: trimmed,
        coins: numCoins,
        category: preset ? preset.category : CUSTOM_META.category,
        icon: preset ? preset.icon : CUSTOM_META.icon,
        color: preset ? preset.color : CUSTOM_META.color,
      });
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-surface-2 border-border max-h-[88vh] overflow-y-auto sm:max-w-lg"
        data-testid="add-line-item-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {editing ? "Edit Task" : "Add Project Task"}
          </DialogTitle>
          <DialogDescription className="sr-only">Pick a template or enter a task and coins.</DialogDescription>
        </DialogHeader>

        {!editing && (
          <>
            <div className="flex rounded-lg bg-surface-3 p-1">
              {["templates", "manual"].map((m) => (
                <button
                  key={m}
                  data-testid={`segment-${m}`}
                  onClick={() => {
                    setMode(m);
                    setError("");
                    if (m === "manual") {
                      setSelectedId(null);
                      setName("");
                      setCoins("");
                    }
                  }}
                  className={`flex-1 rounded-md py-1.5 text-sm font-semibold transition-colors ${
                    mode === m ? "bg-surface-2 text-foreground shadow-sm" : "text-muted-foreground"
                  }`}
                >
                  {m === "templates" ? "Templates" : "Manual"}
                </button>
              ))}
            </div>

            {mode === "templates" && (
              <div className="grid grid-cols-2 gap-2">
                {PRESETS.map((p) => {
                  const Icon = resolveIcon(p.icon);
                  const active = selectedId === p.id;
                  return (
                    <button
                      key={p.id}
                      data-testid={`preset-${p.id}`}
                      onClick={() => pickPreset(p)}
                      className={`rounded-xl border-[1.5px] p-3 text-left transition-colors ${
                        active ? "border-brand bg-brand-tint" : "border-transparent bg-surface-3"
                      }`}
                    >
                      <div
                        className="mb-2 flex h-9 w-9 items-center justify-center rounded-lg"
                        style={{ backgroundColor: `${p.color}1F` }}
                      >
                        <Icon size={18} style={{ color: p.color }} />
                      </div>
                      <p className="truncate text-sm font-semibold text-foreground">{p.name}</p>
                      {catAvg[p.category] != null ? (
                        <p
                          className="font-mono text-xs text-brand tabular"
                          data-testid={`preset-avg-${p.id}`}
                          title="Your average for this category"
                        >
                          ~{formatNum(catAvg[p.category])} avg
                        </p>
                      ) : (
                        <p className="font-mono text-xs text-muted-foreground tabular">{p.defaultCoins} coins</p>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}

        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium text-muted-foreground">Task name</label>
            <Input
              data-testid="input-task-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError("");
              }}
              placeholder="e.g. Login screen"
              className="mt-2 bg-surface border-border"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground">Coins used</label>
            <Input
              data-testid="input-coins"
              value={coins}
              inputMode="numeric"
              onChange={(e) => {
                setCoins(e.target.value.replace(/[^0-9]/g, ""));
                setError("");
              }}
              placeholder="0"
              className="mt-2 bg-surface border-border font-mono"
            />
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {recentCoins.length > 0 && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground" data-testid="recent-coins-label">
                  <History size={12} /> recent
                </span>
              )}
              {coinPicks.map((v) => {
                const isRecent = recentCoins.includes(v);
                return (
                  <button
                    key={v}
                    data-testid={`coin-preset-${v}`}
                    onClick={() => {
                      setCoins(String(v));
                      setError("");
                    }}
                    className={`rounded-full border px-2.5 py-1 font-mono text-xs tabular transition-colors ${
                      isRecent
                        ? "border-brand/60 bg-brand-tint text-brand hover:border-brand"
                        : "border-border bg-surface text-muted-foreground hover:border-brand hover:text-brand"
                    }`}
                  >
                    {formatNum(v)}
                  </button>
                );
              })}
            </div>
          </div>
          {error && (
            <p className="text-sm text-danger" data-testid="add-error">
              {error}
            </p>
          )}
          <Button
            data-testid="add-to-estimate-button"
            onClick={save}
            className="w-full bg-brand hover:bg-brand-dark text-white font-semibold"
          >
            {editing ? "Save Changes" : "Add to Project"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
